'use client';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Carousel, { CarouselProps } from '../Carousel';
import styles from './styles.module.scss';

export type Image = {
    priority?: boolean;
    url: string;
    alt: string;
    caption?: string;
};

export type ImageCarouselProps = Omit<
    CarouselProps,
    'children' | 'index' | 'setIndex' | 'controlled' | 'initialIndex'
> & {
    startingIndex?: number;
    onImageClick?: (image: Image) => void;
    images: Array<Image>;
};

export const ImageCarousel: FunctionComponent<ImageCarouselProps> = ({
    images,
    onImageClick,
    startingIndex,
    ...carouselProps
}) => {
    const [active, setActive] = useState(0);

    useEffect(() => {
        if (startingIndex !== undefined) {
            setActive(startingIndex);
        }
    }, [startingIndex]);

    const carouselItems = useMemo(() => {
        return images.map((image, index) => {
            const onClick = onImageClick ? () => onImageClick(image) : undefined;
            return (
                <div className={styles.image} key={index} onClick={onClick}>
                    <Image
                        src={image.url}
                        alt={image.alt}
                        priority={image.priority}
                        fill
                        sizes="(min-width: 1240px) 750px, 100vw"
                    />
                    <div
                        className={styles.blurred_background_image}
                        style={{ backgroundImage: `url(${image.url})` }}
                    ></div>
                </div>
            );
        });
    }, [images, onImageClick]);

    return (
        <div className={styles.imageCarouselContainer}>
            <Carousel {...carouselProps} controlled={true} index={active} setIndex={setActive}>
                {carouselItems}
            </Carousel>
            <div className={styles.imageCount}>
                {active + 1} / {images.length}
            </div>
        </div>
    );
};
